import { Recording } from '@24sens/ecg01-rest-client';
import { TileComponent } from '@24sens/ui';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Avatar, IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useSignal } from '../signal/useSignalChunk';
import * as selectors from '../store/selectors';
import { useAppSelector } from '../store/store';

const Item = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
  ...theme.typography.body2,
  //padding: theme.spacing(1),
  //textAlign: 'center',
  color: theme.palette.text.secondary,
}));

export const RecordingSummaryCard: React.FC<{ recording: Recording }> = ({ recording }) => {
  const { t } = useTranslation();
  const { patientId } = useParams<{ patientId: string }>();
  const [extended, setExtended] = useState(true);

  const patient = useAppSelector((state) => selectors.patient.selectById(state, patientId));
  const { af, anomaly } = useAppSelector((state) => selectors.signal.labeledChunks(state, recording.id));
  const signalHrStats = useAppSelector((state) => selectors.signal.heartrateStatsForSignal(state, recording.id));
  const signal = useSignal(recording.id);

  return (
    <>
      <TileComponent
        image={<Avatar sx={{ bgcolor: 'var(--twentyfoursens-blue)' }}>{patient?.name?.charAt(0)}</Avatar>}
        primaryText={t('timeline.recordingSummary.title')}
        headerAction={
          <IconButton aria-label="extend" onClick={() => setExtended(!extended)}>
            {extended ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        }
      >
        {extended ? (
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Item>
                {t('timeline.recordingSummary.duration')}:{' '}
                {recording.end_time && recording.start_time
                  ? (
                      (new Date(recording.end_time).getTime() - new Date(recording.start_time).getTime()) /
                      1000 /
                      60 /
                      60
                    ).toFixed(0)
                  : null}{' '}
                h
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                {t('timeline.recordingSummary.hrMean')}: {signalHrStats?.hr_mean.toFixed(0)} BPM
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                {t('timeline.recordingSummary.arrithmyChunks')}: {af?.filter(Boolean).length} (
                {signal?.nr_of_chunks
                  ? ((af?.filter(Boolean).length ?? 0) / (signal?.nr_of_chunks / 100)).toFixed(0)
                  : null}
                %){' '}
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                {t('timeline.recordingSummary.hrMin')}: {signalHrStats?.hr_min.toFixed(0)} BPM
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                {t('timeline.recordingSummary.annomalyChunks')}: {anomaly?.filter(Boolean).length} (
                {signal?.nr_of_chunks
                  ? ((anomaly?.filter(Boolean).length ?? 0) / (signal?.nr_of_chunks / 100)).toFixed(0)
                  : null}
                %)
              </Item>
            </Grid>
            <Grid item xs={6}>
              <Item>
                {t('timeline.recordingSummary.hrMax')}: {signalHrStats?.hr_max.toFixed(0)} BPM
              </Item>
            </Grid>
          </Grid>
        ) : undefined}
      </TileComponent>
      <div style={{ height: '12px' }} />
    </>
  );
};
